import { useEffect, useRef, useState, useCallback } from "react";
import {
  ArrowSquareIcon,
  CopyIcon,
  DownBarIcon,
  EraserIcon,
  InfoIcon,
  RefreshIcon,
  SendIcon,
  SettingsIcon,
  UpBarIcon
} from "../Svg";
import { Button } from "../Button";
import _ from "lodash";
import { TextInput } from "../form/TextInput";
import cx from "classnames";
import Tooltip from "../Tooltip";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { AIShowcase } from "../../store/models/AIShowcase";
import Loader from "../Loader";
import { createSelector } from "reselect";
import { selectMobileLayout } from "../../store/UIState";
import { AiOndemandApi, selectApiKeys } from "../../store/models/AiOndemandApi";
import { selectCurrentUser } from "../../store/models/User";
import { SelectInput } from "../form/SelectInput";
import ReactMarkdown from 'react-markdown';
import { wrapThinkContent } from "../../utils";

const MaxTokens = 2048;
const MinTokens = 1;
const MaxTemp = 2.0;
const MinTemp = 0.0;
const MaxTopP = 1.0;
const MinTopP = 0.1;

const defaultChatMessages = [
  {
    content: "What is Theta Network?",
    role: 'user'
  },
  {
    content: "Theta Network is the leading blockchain decentralized cloud for AI, media and entertainment. Theta Network is a \"dual network\" consisting of two complementary subsystems, the Theta Blockchain and the Theta Edge Network. The Theta blockchain provides payment, reward, and smart contract capabilities, while the edge network provides vast amounts of GPU compute power for AI, video, rendering and other tasks. Theta EdgeCloud, slated to launch in 2024 is the first hybrid cloud-edge AI computing platform powered by the Theta Edge Network with over 10,000 active global nodes. Theta blockchain supports Turing complete smart contracts, and is fully compatible with Ethereum. This EVM support enables a wide range of interesting Web3 applications to be built on the Theta Network. Theta's enterprise validator and governance council is led by Google, Samsung, Sony, Creative Artists Agency (CAA), Binance, Blockchain Ventures, DHVC, gumi and other global leaders. Theta has partnered with leading entertainment brands including Lionsgate, MGM, Katy Perry, American Idol, The Price is Right, Taste of Home, and more. Popular platforms utilizing Theta's Web3 infrastructure include MetaCannes Film3 Festival, FuseTV, CONtv Anime, WPT, PetCollective, FailArmy, and other OTT streaming services.",
    role: 'assistant'
  },
]

const ModelTitleMap = {
  'llama-3-1-70b': 'Model: Llama 3.1 70B Instruct',
  'llama-3-8b': 'Model: Llama 3 8B Instruct',
  'deepseek-r1': 'Model: Deepseek R1 / Distill-Qwen-7B'
}

const SettingsPanel = ({ onParamsChanged, modelOptions, currentModel, onModelChange }) => {
  const [maxTokens, setMaxTokens] = useState(500);
  const [temperature, setTemperature] = useState(0.5);
  const [topP, setTopP] = useState(0.7);

  useEffect(() => {
    const rangeMaxTokens = document.getElementById('maxTokens');
    const rangeTemperature = document.getElementById('temperature');
    const rangeTopP = document.getElementById('topP');
    rangeMaxTokens.addEventListener('input', updateGradient);
    rangeTemperature.addEventListener('input', updateGradient);
    rangeTopP.addEventListener('input', updateGradient);

    updateGradient({ target: rangeMaxTokens });
    updateGradient({ target: rangeTemperature });
    updateGradient({ target: rangeTopP });

    return () => {
      rangeMaxTokens.removeEventListener('input', updateGradient);
      rangeTemperature.removeEventListener('input', updateGradient);
      rangeTopP.removeEventListener('input', updateGradient);
    };
  }, []);

  useEffect(() => {
    onParamsChanged(maxTokens, temperature, topP)
  }, [maxTokens, temperature, topP]);

  function updateGradient(e) {
    const range = e.target;
    const value = ((range.value - range.min) / (range.max - range.min)) * 100;
    const color = `linear-gradient(to right, #129978 ${value}%, #3D4463 ${value}%)`;

    // Create a new style element
    const style = document.createElement('style');

    style.textContent = `
    #${range.id}::-moz-range-track {
      background: ${color};
    }
    #${range.id}::-webkit-slider-runnable-track {
      background: ${color};
    }
  `;

    document.head.appendChild(style);
  }

  const onSetMaxTokens = (event) => {
    const value = event.target.value;
    if (_.isEmpty(value)) {
      setMaxTokens(0)
    } else if (value < MinTokens) {
      setMaxTokens(MinTokens)
    } else if (value > MaxTokens) {
      setMaxTokens(MaxTokens)
    } else {
      setMaxTokens(value)
    }
  }

  const onSetTemperature = (event) => {
    const value = event.target.value;
    if (value < MinTemp) {
      setTemperature(MinTemp)
    } else if (value > MaxTemp) {
      setTemperature(MaxTemp)
    } else {
      setTemperature(value)
    }
  }

  const onSetTopP = (event) => {
    const value = event.target.value;
    if (_.isEmpty(value)) {
      setTopP(0)
    } else if (value < MinTopP) {
      setTopP(MinTopP)
    } else if (value > MaxTopP) {
      setTopP(MaxTopP)
    } else {
      setTopP(value)
    }
  }

  return (
    <div className="SettingsPanel">


      <div className={"SettingsPanel__InputsWrapper"}>

        {modelOptions && modelOptions.length > 1 ? (
          <div className="SettingsPanel__field">
            <div className={"SettingsPanel__field--top"}>
              <div className="label-wrapper"><label>model</label></div>
            </div>
            <SelectInput
              className={"SettingsPanel__Select"}
              options={modelOptions}
              value={{ label: currentModel?.name || "-", value: currentModel }}
              onChange={onModelChange}
            />
          </div>
        ) : (
          currentModel?.name && <div className="SettingsPanel__field">
            <div className={"SettingsPanel__field--top"}>
              <div className="SettingsPanel__field--top">
                <label>{currentModel?.name || "-"}</label>
              </div>
            </div>
          </div>
        )}

        <div className="SettingsPanel__field">
          <div className={"SettingsPanel__field--top"}>
            <Tooltip longText={true}
              tooltip={"The maximum number of tokens that can be generated in the chat completion."}>
              <div className={"label-wrapper"}>
                <label htmlFor="maxTokens">max_tokens </label>
                <InfoIcon />
              </div>
            </Tooltip>
            <TextInput max={MaxTokens} min={MinTokens} type={"number"} value={maxTokens} onChange={onSetMaxTokens} />
          </div>
          <input type="range" id="maxTokens" max={MaxTokens} min={MinTokens} value={maxTokens}
            onChange={onSetMaxTokens} />
        </div>
        <div className="SettingsPanel__field">
          <div className={"SettingsPanel__field--top"}>
            <Tooltip longText={true}
              tooltip={"Controls the randomness of the ouptput. A high temperature produces more unpredictable results and a lower temperature creates more deterministic outputs"}>
              <div className={"label-wrapper"}>
                <label htmlFor="temperature">temperature</label>
                <InfoIcon />
              </div>
            </Tooltip>
            <TextInput step={0.1} max={MaxTemp} min={MinTemp} type={"number"} value={temperature}
              onChange={onSetTemperature} />
          </div>
          <input step={0.1} type="range" id="temperature" max={MaxTemp} min={MinTemp} value={temperature}
            onChange={onSetTemperature} />
        </div>
        <div className="SettingsPanel__field">
          <div className={"SettingsPanel__field--top"}>
            <Tooltip longText={true}
              tooltip={"An alternative to sampling with temperature, called nucleus sampling, where the model considers the results of the tokens with top_p probability mass. So 0.1 means only the tokens comprising the top 10% probability mass are considered."}>
              <div className={"label-wrapper"}>
                <label htmlFor="maxTokens">top_p</label>
                <InfoIcon />
              </div>
            </Tooltip>
            <TextInput step={0.1} max={MaxTopP} min={MinTopP} type={"number"} value={topP} onChange={onSetTopP} />
          </div>
          <input step={0.1} type="range" id="topP" max={MaxTopP} min={MinTopP} value={topP}
            onChange={onSetTopP} />
        </div>
      </div>
    </div>
  );
};

const ChatMessage = ({ message, index, onRegenerate, mName }) => {

  const onCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(message.content);
    toast.success("Copied to clipboard")
  }

  const onRegenerateClicked = (e) => {
    e.stopPropagation();
    onRegenerate(message, index);
  }

  const formattedContent = formatWithParagraphs(message.content || message.error);
  // {message.role === 'assistant' ? wrapThinkContent(formattedContent, ) : formattedContent || message.error}

  return (
    <div className="ChatbotMessageContainer">
      <div className={`ChatbotMessage ChatbotMessage--${message.role} ${message.error ? 'ChatbotMessage__error' : ''}`}>
        <div className="ChatbotMessage__content">
          {message.role === 'assistant' && mName === 'deepseek-r1'
            ? (wrapThinkContent(formattedContent, true) || message.error)
            : <ReactMarkdown>
              {formattedContent}
            </ReactMarkdown>}
        </div>
        <div className={`ChatbotMessage__actions ChatbotMessage__actions--${message.role}`}>
          <Tooltip tooltip={"Copy"}>
            <div onClick={onCopy}><CopyIcon /></div>
          </Tooltip>
          {message.role === 'assistant' &&
            <Button title={"Regenerate"}
              onClick={onRegenerateClicked}
              color={"grey-outline"}
              icon={<RefreshIcon />}
            />}
        </div>
      </div>
    </div>
  )
}

const selector = createSelector(
  selectMobileLayout,
  selectCurrentUser,
  (isMobile, curUser) => ({ isMobile, curUser })
)

// Function to add paragraph breaks to long text blocks
const formatWithParagraphs = (text) => {
  if (!text || typeof text !== 'string') return text;

  // If the text already has proper paragraph breaks (double line breaks), return as is
  if (text.includes('\n\n')) return text;

  // Split by sentences and group them into paragraphs
  const sentences = text.split(/([.!?]+\s+)/);
  let formattedText = '';
  let currentParagraph = '';
  let sentenceCount = 0;

  for (let i = 0; i < sentences.length; i += 2) {
    const sentence = sentences[i];
    const punctuation = sentences[i + 1] || '';

    if (sentence && sentence.trim()) {
      currentParagraph += sentence + punctuation;
      sentenceCount++;

      // Add paragraph break after 2-4 sentences or if we hit certain keywords
      const shouldBreak = sentenceCount >= 3 ||
        sentence.toLowerCase().includes('**') || // After bold headers
        sentence.length > 200; // Very long sentences

      if (shouldBreak && currentParagraph.trim()) {
        formattedText += currentParagraph.trim() + '\n\n';
        currentParagraph = '';
        sentenceCount = 0;
      }
    }
  }

  // Add any remaining content
  if (currentParagraph.trim()) {
    formattedText += currentParagraph.trim();
  }

  return formattedText;
};

export const ShowcaseChatbot = ({ showcase, className, mName = null }) => {
  const dispatch = useDispatch();
  const { isMobile, curUser } = useSelector(state => selector(state));
  const [textareaValue, setTextareaValue] = useState('')
  const [messages, setMessages] = useState(defaultChatMessages);
  const [regeneratingMessageIndex, setRegeneratingMessageIndex] = useState(null);
  const [showSettings, setShowSettings] = useState(!isMobile)
  const [settings, setSettings] = useState({ maxTokens: 500, temperature: 0.5, topP: 0.7 })
  const chatboxRef = useRef(null);
  const [streamingResponse, setStreamingResponse] = useState(null);
  const apiKeys = useSelector(state => selectApiKeys(state));
  const apiKey = _.get(apiKeys, '0.key');

  // Add state for current model and available models
  const availableModels = showcase?.models || [];
  const [currentModel, setCurrentModel] = useState(availableModels[0] || null);
  const modelOptions = availableModels.map(model => ({ label: model.name, value: model }));
  const submitDisabled = _.isEmpty(textareaValue) || streamingResponse !== null;

  const onTogglePanel = () => {
    setShowSettings(!showSettings);
  }

  const sendMessage = async (event) => {
    event.preventDefault();
    if (isMobile) {
      setShowSettings(false)
    }

    const newMessage = { content: textareaValue, role: 'user' }
    setMessages([...messages, newMessage]);

    try {
      setTextareaValue('')
      setStreamingResponse('');
      let params = {
        messages: [...messages, newMessage],
        max_tokens: settings.maxTokens,
        temperature: settings.temperature,
        top_p: settings.topP,
        stream: true
      };
      if (!mName) {
        await dispatch(AIShowcase.actions.createCompletion(currentModel, params, false, onResponseEvent));
      } else {
        await dispatch(AiOndemandApi.actions.hostedCreateCompletion(params, apiKey, onResponseEvent, mName, curUser));
      }
    } catch (e) {
      toast.error(e.message)
      console.log(e);
      setStreamingResponse(null);
    }
  }

  const onTextareaChange = (event) => {
    setTextareaValue(event.target.value)
  }

  const onResponseEvent = (event) => {
    switch (event.type) {
      case 'chunk':
        setStreamingResponse((streamingResponse) => streamingResponse + event.content)
        break;
      case 'done':
        // const styledContent = wrapThinkContent(event.content, mName === 'deepseek-r1');
        setRegeneratingMessageIndex((index) => {
          if (index) {
            setMessages((messages) => {
              let newMessages = [...messages];
              if (index >= newMessages.length) {
                newMessages.push({ content: event.content, role: 'assistant' })
              } else {
                const newMessage = { content: event.content, role: 'assistant' };
                newMessages = [...newMessages.slice(0, index), newMessage, ...newMessages.slice(index)];
              }
              return newMessages;
            })
          } else {
            setMessages((messages) => [...messages, { content: event.content, role: 'assistant' }])
            return null;
          }
        })
        setStreamingResponse(null)
        break;
      case 'error':
        setMessages((messages) => [...messages, { error: event.error, role: 'assistant' }])
        setStreamingResponse(null);
        break;
    }
  }

  useEffect(() => {
    if (!regeneratingMessageIndex || regeneratingMessageIndex >= messages.length) {
      onScrollToBottom();
    }
  }, [messages, streamingResponse, regeneratingMessageIndex]);

  const onScrollToBottom = () => {
    if (isMobile) {
      window.scrollTo(0, document.body.scrollHeight);
    } else {
      chatboxRef.current.scrollTop = chatboxRef.current.scrollHeight;
    }
  }

  const onScrollToTop = () => {
    if (isMobile) {
      window.scrollTo(0, 0);
    } else {
      chatboxRef.current.scrollTop = 0;
    }
  }

  const onClearMessages = () => {
    setMessages([]);
  }

  const onSettingsChanged = (maxTokens, temperature, topP) => {
    setSettings({ maxTokens, temperature, topP })
  }

  const regenerateMessage = async (message, index) => {
    try {
      setMessages([...messages.slice(0, index),
      ...messages.slice(index + 1)
      ]);

      setStreamingResponse('');

      setRegeneratingMessageIndex(index)
      const params = {
        messages: messages.slice(0, index),
        max_tokens: settings.maxTokens,
        temperature: settings.temperature,
        top_p: settings.topP,
        stream: true
      }
      if (!mName) {
        await dispatch(AIShowcase.actions.createCompletion(currentModel, params, true, onResponseEvent));
      } else {
        await dispatch(AiOndemandApi.actions.hostedCreateCompletion(params, apiKey, onResponseEvent, mName));
      }

    } catch (e) {
      console.log(e)
      setStreamingResponse(null);
    }
  }

  const handleModelChange = (option) => {
    setCurrentModel(option.value);
  };

  return (
    <div className={cx("ShowcaseChatbot", className)}>
      <div className="ShowcaseChatbot__container">
        {showSettings && (
          <SettingsPanel
            onParamsChanged={onSettingsChanged}
            modelOptions={!mName ? modelOptions : null}
            currentModel={currentModel}
            onModelChange={handleModelChange}
          />
        )}

        <div className={"ShowcaseChatbot__LeftInfo"}>
          <div className={"SettingsPanel__Title"}>

          </div>
          {!isMobile && <ShowOptionsButton isMobile={isMobile} onTogglePanel={onTogglePanel} showSettings={showSettings} />}
        </div>

        <div ref={isMobile ? chatboxRef : null} className={cx("ShowcaseChatbot__chatbox", !showSettings && "ShowcaseChatbot__chatbox--no-settings")}>
          <div ref={!isMobile ? chatboxRef : null} className="ShowcaseChatbot__messages">
            {messages.map((message, index) => {
              if (regeneratingMessageIndex && index === regeneratingMessageIndex) {
                return (
                  <>
                    {streamingResponse !== null && <div className="ChatbotMessageContainer" key={index + '_'}>
                      <div className={`ChatbotMessage ChatbotMessage--assistant`}>
                        <div className="ChatbotMessage__content">
                          {streamingResponse === '' && <Loader size="small" />}
                          {streamingResponse && <ReactMarkdown>{formatWithParagraphs(streamingResponse)}</ReactMarkdown>}
                        </div>
                      </div>
                    </div>}
                    <ChatMessage key={index} index={index} message={message} onRegenerate={regenerateMessage} mName={mName} />
                  </>
                )
              }
              return (
                <ChatMessage key={index} index={index} message={message} onRegenerate={regenerateMessage} mName={mName} />
              )
            })}
            {streamingResponse !== null && (!regeneratingMessageIndex || regeneratingMessageIndex >= messages.length) && (
              <div className="ChatbotMessageContainer">
                <div className="ChatbotMessage ChatbotMessage--assistant">
                  <div className="ChatbotMessage__content">
                    {streamingResponse === '' && <Loader size="small" />}
                    {streamingResponse && <ReactMarkdown>{formatWithParagraphs(streamingResponse)}</ReactMarkdown>}
                  </div>
                </div>
              </div>
            )}
          </div>
          <form className="ShowcaseChatbot__form" onSubmit={sendMessage}>
            <textarea name="message"
              className="ShowcaseChatbot__input"
              value={textareaValue}
              onChange={onTextareaChange}
              onKeyDown={(event) => {
                if (event.shiftKey && event.key === 'Enter' || event.altKey && event.key === 'Enter') {
                  event.preventDefault();
                  //add new line
                  setTextareaValue(textareaValue + '\n')
                } else if (event.key === 'Enter') {
                  event.preventDefault();
                  if (!submitDisabled) {
                    sendMessage(event);
                  }
                }
              }}
              placeholder="Type something... Enter to send." />

            <div className={"ShowcaseChatbot__InputActions"}>
              {isMobile && <ShowOptionsButton isMobile={isMobile} onTogglePanel={onTogglePanel} showSettings={showSettings} />}
              <Tooltip tooltip={"Scroll to bottom"}>
                <div onClick={onScrollToBottom}><DownBarIcon /></div>
              </Tooltip>
              <Tooltip tooltip={"Scroll to top"}>
                <div onClick={onScrollToTop}><UpBarIcon /></div>
              </Tooltip>
              <Tooltip tooltip={"Clear all messages"}>
                <div onClick={onClearMessages}><EraserIcon /></div>
              </Tooltip>
            </div>

            <Button
              title={"Send"}
              type="submit"
              icon={<SendIcon />}
              className="ShowcaseChatbot__button"
              color={"green"}
              size={"small"}
              disabled={submitDisabled} />
          </form>
        </div>
      </div>
    </div>
  );
}

const ShowOptionsButton = ({ onTogglePanel, showSettings, isMobile }) => {
  return (<div
    className={cx("ShowcaseChatbot__ShowOptionsBtn", !showSettings && "ShowcaseChatbot__ShowOptionsBtn--reverse")}
    onClick={onTogglePanel}>
    {isMobile ? <SettingsIcon /> : <ArrowSquareIcon />}
    {showSettings ? "Hide options" : "Show options"}
  </div>)
}